import type * as opentype from 'opentype.js';

import type { TextLine } from '../types';

export interface PositionedGlyph {
  char: string;
  charIndex: number;
  lineIndex: number;
  pathData: string;
  bounds: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    width: number;
    height: number;
  };
  isFloating: boolean;
}

/**
 * Lays out multi-line typography with exact physical millimeter coordinates.
 * Generates clean, mathematically exact Bézier curves directly from OpenType glyphs.
 */
export function layoutTextLines(
  lines: TextLine[],
  fontMap: Map<string, opentype.Font>,
  lineSpacingMultiplier = 1.15,
  overallAlignment: 'left' | 'center' | 'right' = 'center',
): {
  allGlyphs: PositionedGlyph[];
  bounds: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    widthMm: number;
    heightMm: number;
  };
} {
  interface LineMeasure {
    lineIndex: number;
    lineWidthMm: number;
    fontSizeMm: number;
    glyphs: Array<{
      glyph: opentype.Glyph;
      char: string;
      charIndex: number;
      localX: number;
      advance: number;
    }>;
  }

  const measuredLines: LineMeasure[] = [];

  // Pass 1: Measure each line's width and local glyph offsets
  for (let lIdx = 0; lIdx < lines.length; lIdx++) {
    const line = lines[lIdx];
    if (!line.text.trim()) continue;

    const font = fontMap.get(line.fontId);
    if (!font) continue;

    const fontSize = line.fontSizeMm;
    const scale = fontSize / font.unitsPerEm;
    // Tracking multiplier: e.g. -4% -> 0.96 (for cursive welding overlap)
    const trackingMultiplier = 1 + line.letterSpacingPercent / 100;

    const opentypeGlyphs = font.stringToGlyphs(line.text);
    let curX = 0;
    const lineGlyphs: LineMeasure['glyphs'] = [];

    for (let cIdx = 0; cIdx < opentypeGlyphs.length; cIdx++) {
      const g = opentypeGlyphs[cIdx];
      const char = line.text[cIdx] || '';

      if (!char.trim()) {
        const spaceAdvance =
          (g.advanceWidth || font.unitsPerEm * 0.35) * scale * trackingMultiplier;
        curX += spaceAdvance;
        continue;
      }

      let kerning = 0;
      if (cIdx < opentypeGlyphs.length - 1) {
        kerning = font.getKerningValue(g, opentypeGlyphs[cIdx + 1]);
      }

      lineGlyphs.push({
        glyph: g,
        char,
        charIndex: cIdx,
        localX: curX,
        advance: (g.advanceWidth || font.unitsPerEm * 0.5) * scale,
      });

      curX += ((g.advanceWidth || font.unitsPerEm * 0.5) + kerning * scale) * trackingMultiplier;
    }

    measuredLines.push({
      lineIndex: lIdx,
      lineWidthMm: curX,
      fontSizeMm: fontSize,
      glyphs: lineGlyphs,
    });
  }

  if (measuredLines.length === 0) {
    return {
      allGlyphs: [],
      bounds: { minX: 0, minY: 0, maxX: 100, maxY: 40, widthMm: 100, heightMm: 40 },
    };
  }

  const maxLineWidth = Math.max(...measuredLines.map((l) => l.lineWidthMm), 10);
  const allGlyphs: PositionedGlyph[] = [];
  let currentBaselineY = 0;

  // Pass 2: Place glyphs at final world coordinates and extract Bezier paths
  for (let i = 0; i < measuredLines.length; i++) {
    const mLine = measuredLines[i];
    const lineObj = lines[mLine.lineIndex];
    const font = fontMap.get(lineObj.fontId)!;
    const align = lineObj.alignment || overallAlignment;

    // Line vertical height
    if (i === 0) {
      currentBaselineY = mLine.fontSizeMm * 0.85;
    } else {
      currentBaselineY += mLine.fontSizeMm * lineSpacingMultiplier;
    }

    // Alignment horizontal offset
    let xOffset = 0;
    if (align === 'center') {
      xOffset = (maxLineWidth - mLine.lineWidthMm) / 2;
    } else if (align === 'right') {
      xOffset = maxLineWidth - mLine.lineWidthMm;
    }

    for (const gInfo of mLine.glyphs) {
      const worldX = xOffset + gInfo.localX;
      const worldY = currentBaselineY + (lineObj.yOffsetMm || 0);

      // Extract uncorrupted Bezier path directly at world coordinates
      const otPath = gInfo.glyph.getPath(worldX, worldY, mLine.fontSizeMm);
      const pathData = otPath.toPathData(2);

      const bbox = gInfo.glyph.getBoundingBox();
      const scale = mLine.fontSizeMm / font.unitsPerEm;
      const minX = worldX + bbox.x1 * scale;
      const maxX = worldX + bbox.x2 * scale;
      const minY = worldY - bbox.y2 * scale;
      const maxY = worldY - bbox.y1 * scale;

      const isFloating =
        (gInfo.char === 'i' ||
          gInfo.char === 'j' ||
          gInfo.char === '!' ||
          gInfo.char === '?' ||
          gInfo.char === ':' ||
          gInfo.char === ';') &&
        bbox.y1 * scale > mLine.fontSizeMm * 0.2;

      if (pathData && pathData.length > 5) {
        allGlyphs.push({
          char: gInfo.char,
          charIndex: gInfo.charIndex,
          lineIndex: mLine.lineIndex,
          pathData,
          bounds: {
            minX: round(minX),
            minY: round(minY),
            maxX: round(maxX),
            maxY: round(maxY),
            width: round(Math.max(0.1, maxX - minX)),
            height: round(Math.max(0.1, maxY - minY)),
          },
          isFloating,
        });
      }
    }
  }

  if (allGlyphs.length === 0) {
    return {
      allGlyphs: [],
      bounds: { minX: 0, minY: 0, maxX: 100, maxY: 40, widthMm: 100, heightMm: 40 },
    };
  }

  const minX = Math.min(...allGlyphs.map((g) => g.bounds.minX));
  const maxX = Math.max(...allGlyphs.map((g) => g.bounds.maxX));
  const minY = Math.min(...allGlyphs.map((g) => g.bounds.minY));
  const maxY = Math.max(...allGlyphs.map((g) => g.bounds.maxY));

  return {
    allGlyphs,
    bounds: {
      minX: round(minX),
      minY: round(minY),
      maxX: round(maxX),
      maxY: round(maxY),
      widthMm: round(Math.max(1, maxX - minX)),
      heightMm: round(Math.max(1, maxY - minY)),
    },
  };
}

function round(n: number, decimals = 2): number {
  const factor = Math.pow(10, decimals);
  return Math.round(n * factor) / factor;
}
